import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "disque-dur-sature",
  silo: "performance",
  h1: "Disque dur saturé : comment libérer de l'espace sur Windows ?",
  metaTitle: "Disque dur plein Windows : libérer de l'espace facilement",
  metaDescription: "Votre disque dur est plein et votre PC ralentit ? Suivez notre guide pour libérer de l'espace sans perdre vos fichiers importants.",
  excerpt: "Un disque dur plein est l'une des premières causes de lenteur sur Windows. Heureusement, il suffit souvent de quelques nettoyages ciblés pour retrouver plusieurs gigaoctets d'espace -- sans rien supprimer d'important.",
  publishedAt: "2024-03-12",
  readingMinutes: 5,
  relatedSlugs: ["pc-lent-windows-10-11", "demarrage-lent-windows", "recuperer-fichiers-supprimes"],
  faq: [
    {
      question: "Quelle quantité d'espace libre faut-il garder sur son disque ?",
      answer: "Windows recommande de garder au moins 10 à 15% d'espace libre. Sur un disque de 500 Go, cela représente environ 50 à 75 Go de libre. En dessous, les performances se dégradent nettement.",
    },
    {
      question: "Les fichiers dans la corbeille prennent-ils de la place ?",
      answer: "Oui ! Les fichiers supprimés restent dans la corbeille et continuent d'occuper de l'espace jusqu'à ce que vous la vidiez. Pensez à vider régulièrement la corbeille.",
    },
    {
      question: "Peut-on supprimer le dossier Windows.old ?",
      answer: "Oui, sans risque. Ce dossier contient l'ancienne version de Windows conservée après une mise à jour. Il peut peser 20 à 30 Go et peut être supprimé via l'outil Nettoyage de disque (chercher 'fichiers système').",
    },
    {
      question: "Que faire si je n'arrive pas à libérer suffisamment d'espace ?",
      answer: "Si votre disque est structurellement trop petit pour vos besoins, le remplacement par un SSD plus grand est la solution la plus efficace. Nos techniciens peuvent cloner votre disque sans perte de données.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Windows a besoin d'espace libre sur le disque pour créer des fichiers temporaires, gérer la mémoire virtuelle et installer des mises à jour. Quand le disque est saturé, tout ralentit -- parfois jusqu'au blocage complet. Voici comment récupérer de l'espace sans risque.",
    },
    {
      type: "h2",
      text: "Comment voir ce qui occupe l'espace sur votre disque",
    },
    {
      type: "p",
      text: "Windows propose un outil intégré très pratique : allez dans Paramètres > Système > Stockage. Vous verrez immédiatement la répartition de l'espace utilisé : applications, fichiers temporaires, musique, vidéos, etc.",
    },
    {
      type: "p",
      text: "Pour une analyse plus détaillée, le logiciel gratuit WinDirStat affiche une carte visuelle de votre disque avec les dossiers et fichiers les plus volumineux. Idéal pour trouver les gros fichiers oubliés.",
    },
    {
      type: "h2",
      text: "7 façons de libérer de l`espace rapidement",
    },
    {
      type: "ol",
      items: [
        "Vider la corbeille (clic droit sur l'icône bureau > Vider la corbeille)",
        "Utiliser le Nettoyage de disque (cherchez 'Nettoyage' dans le menu Démarrer)",
        "Supprimer Windows.old via Nettoyage de disque > Nettoyer les fichiers système",
        "Désinstaller les logiciels inutilisés (Paramètres > Applications)",
        "Déplacer photos et vidéos vers un disque externe ou le cloud (OneDrive, Google Photos)",
        "Supprimer les gros fichiers de téléchargement oubliés dans le dossier Téléchargements",
        "Activer la fonctionnalité 'Stockage optimisé' de Windows pour le nettoyage automatique",
      ],
    },
    {
      type: "callout",
      variant: "tip",
      title: "Astuce",
      text: "Le dossier Téléchargements est souvent le plus encombré. Triez-le par taille (clic droit > Trier par > Taille) pour identifier rapidement les gros fichiers à supprimer.",
    },
    {
      type: "h2",
      text: "Le lien entre disque saturé et lenteur",
    },
    {
      type: "p",
      text: "Quand le disque est à plus de 85% de sa capacité, les performances de Windows chutent visiblement. Le système ne trouve plus d'espace pour écrire ses fichiers temporaires, ce qui provoque un [PC lent](slug:pc-lent-windows-10-11) et un [démarrage très lent](slug:demarrage-lent-windows).",
    },
    {
      type: "h2",
      text: "Disque plein malgré le nettoyage : que faire ?",
    },
    {
      type: "p",
      text: "Si votre disque reste saturé malgré le nettoyage, deux solutions s'offrent à vous : acheter un disque dur externe pour décharger vos fichiers, ou remplacer votre disque par un modèle plus grand. Dans ce cas, pensez à un SSD : plus rapide, plus silencieux, et les prix ont beaucoup baissé. Nos techniciens peuvent se déplacer à domicile ou sur site pour effectuer le clonage de disque.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Ne supprimez pas n'importe quoi",
      text: "Évitez de supprimer des fichiers dans les dossiers Windows, System32 ou Program Files. Si vous n'êtes pas sûr, demandez à un technicien avant de supprimer. En cas de suppression accidentelle, consultez notre guide sur la [récupération de fichiers supprimés](slug:recuperer-fichiers-supprimes).",
    },
  ],
};

export default article;
